/**
 * SDK 客服挂件核心(轻量重写,不引 zustand)
 *
 * 镜像 hotel-hub 的 CustomerServiceWidget 副作用,但:
 * - 不依赖 useUserStore(zustand),改用 shared-storage 纯函数读同源登录态
 * - 无 UI(气泡由 widget.js 自挂 document.body),故不挂 Shadow DOM、不引 React
 * - 样式经 data-* 属性透传(文档场景 E)
 *
 * 登录态感知:挂载时读 + 2s 轮询 + storage 事件(跨 tab)。
 * - 已登录:widget ready 后 identify(userId/name/avatar/token)
 * - 登出(true->false):destroy + 重载 widget,恢复匿名
 * - 登录(false->true)/换号(token 变):identify
 *
 * 单例:全局只一个客服 widget(window.AgentDesk + widget.js 按 src 去重)。
 */
import {
  readUserStorage,
  readUserToken,
  readIsLoggedIn,
  USER_STORAGE_KEY,
} from '@/lib/shared-storage';
import { fetchCustomerServiceConfig } from '../api/customer-service';
import { getConfig } from '../config';
import type {
  CustomerServiceStyle,
  CreateCustomerServiceOptions,
  CustomerServiceHandle,
} from '../types';

/** widget.js loader 地址(独立于 chatUrl,与 hotel-hub 侧一致,见 CustomerServiceWidget 注释) */
const DEFAULT_WIDGET_SCRIPT_SRC = 'https://desk-site.roomgenie.cn/widget.js';
const SCRIPT_ID = 'agent-desk-widget';

/** 本次官网默认 preset:悬浮 + 胶囊文字 + 竖向 + 边距 Y150 */
export const DEFAULT_CS_STYLE: CustomerServiceStyle = {
  variant: 'pill',
  label: '在线客服',
  layout: 'vertical',
  offsetY: 150,
  position: 'bottom-right',
  icon: 'chat',
  size: 56,
  shape: 'circle',
};

/** AgentDesk 最小 API 形状 */
interface AgentDeskAPI {
  on?(event: string, cb: () => void): void;
  identify?(payload: {
    userId: string;
    name: string;
    avatar: string;
    phone: string;
    token: string;
  }): void;
  destroy?(): void;
}

function getAgentDesk(): AgentDeskAPI | undefined {
  if (typeof window === 'undefined') return undefined;
  return (window as unknown as { AgentDesk?: AgentDeskAPI }).AgentDesk;
}

/** 样式字段 -> data-* 属性名(不含 data- 前缀) */
const STYLE_TO_DATA: Record<keyof CustomerServiceStyle, string> = {
  color: 'color',
  position: 'position',
  icon: 'icon',
  size: 'size',
  shape: 'shape',
  variant: 'variant',
  label: 'label',
  layout: 'layout',
  offsetX: 'offset-x',
  offsetY: 'offset-y',
  precedence: 'precedence',
};

/** 当前单例 handle(全局只一个客服 widget) */
let currentHandle: CustomerServiceHandle | null = null;

/**
 * 挂载客服挂件。tenantId/chatUrl 缺失(未开通)则不挂载,返回空 handle。
 * 样式缺省用 DEFAULT_CS_STYLE。
 */
export function mountCustomerService(opts: CreateCustomerServiceOptions): CustomerServiceHandle {
  // 单例:已有先 destroy
  if (currentHandle) {
    currentHandle.destroy();
    currentHandle = null;
  }

  const { tenantId, chatUrl, scriptSrc, style = DEFAULT_CS_STYLE, onReady } = opts;

  // 未开通(字段缺失):不挂载,返回空 handle(不占单例位,以便 autoMount 后续仍可挂)
  if (!tenantId || !chatUrl) {
    return { destroy() {}, refresh() {} };
  }

  // 显式窄化为 string:TS 不会把控制流窄化传播进下方闭包(injectScript 等),故在此赋值。
  const tenant = tenantId;
  const chat = chatUrl;

  const src = scriptSrc || DEFAULT_WIDGET_SCRIPT_SRC;
  let ready = false;
  let destroyed = false;
  let lastLoggedIn = readIsLoggedIn();
  let lastToken = readUserToken();
  let pollTimer: ReturnType<typeof setInterval> | null = null;

  function injectScript(): void {
    try {
      getAgentDesk()?.destroy?.();
    } catch {
      // 静默
    }
    document.getElementById(SCRIPT_ID)?.remove();
    ready = false;

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = src;
    // async=false 保证 currentScript 指向本元素,widget.js 才能读到 data-* 属性
    // (动态插入的 script 默认 async,执行时 currentScript 为 null;见 hotel-hub 侧 CustomerServiceWidget 注释)
    script.async = false;
    script.setAttribute('data-tenant', tenant);
    script.setAttribute('data-chat-url', chat);
    for (const [key, dataKey] of Object.entries(STYLE_TO_DATA)) {
      const v = style[key as keyof CustomerServiceStyle];
      if (v != null) script.setAttribute(`data-${dataKey}`, String(v));
    }

    script.onload = () => {
      if (destroyed) return;
      const ag = getAgentDesk();
      if (!ag) {
        console.error('[HotelSDK] customer-service: widget.js 加载后未挂载 window.AgentDesk');
        return;
      }
      if (typeof ag.on === 'function') {
        ag.on('ready', () => {
          if (destroyed) return;
          ready = true;
          identifyIfReady();
          onReady?.();
        });
      } else {
        // widget.js 加载但未提供 on(异常),兜底标记就绪以不阻塞 identify
        ready = true;
        identifyIfReady();
        onReady?.();
      }
    };
    script.onerror = () => {
      if (!destroyed) {
        console.error('[HotelSDK] customer-service: widget.js 加载失败:', src);
      }
    };
    document.body.appendChild(script);
  }

  function identifyIfReady(): void {
    if (!ready || destroyed) return;
    const ag = getAgentDesk();
    if (!ag?.identify) return;
    const s = readUserStorage();
    if (s?.isLoggedIn && s.user) {
      ag.identify({
        userId: String(s.user.userID),
        name: s.user.nickName || s.user.mobile || '',
        avatar: s.user.avatar || '',
        phone: s.user.mobile || '',
        token: s.token || '',
      });
    }
  }

  function reload(): void {
    if (destroyed) return;
    // destroy widget + 移除 script + 重新注入,恢复匿名会话
    injectScript();
  }

  /** 检测登录态变化并响应(轮询与 storage 事件共用) */
  function checkAuthChange(): void {
    if (destroyed) return;
    const nowLoggedIn = readIsLoggedIn();
    const nowToken = readUserToken();
    if (nowLoggedIn !== lastLoggedIn) {
      if (nowLoggedIn)
        identifyIfReady(); // false -> true
      else reload(); // true -> false 恢复匿名
    } else if (nowLoggedIn && nowToken !== lastToken) {
      identifyIfReady(); // 同登录态但 token 变(换号)
    }
    lastLoggedIn = nowLoggedIn;
    lastToken = nowToken;
  }

  function onStorage(e: StorageEvent): void {
    if (e.key === USER_STORAGE_KEY) checkAuthChange();
  }

  // 启动
  injectScript();
  pollTimer = setInterval(checkAuthChange, 2000);
  window.addEventListener('storage', onStorage);

  const handle: CustomerServiceHandle = {
    destroy() {
      if (destroyed) return;
      destroyed = true;
      if (pollTimer) {
        clearInterval(pollTimer);
        pollTimer = null;
      }
      window.removeEventListener('storage', onStorage);
      try {
        getAgentDesk()?.destroy?.();
      } catch {
        // 静默
      }
      document.getElementById(SCRIPT_ID)?.remove();
      ready = false;
      if (currentHandle === handle) currentHandle = null;
    },
    refresh() {
      checkAuthChange();
    },
  };
  currentHandle = handle;
  return handle;
}

/**
 * 自动挂载(默认开):SDK 加载后由 index.ts 调用。
 * - customerService 开关关 -> 不挂
 * - 页面已有 <hotel-customer-service> 或命令式已挂(currentHandle)-> 跳过
 * - fetchCustomerServiceConfig 拿客服配置(同源 BFF),未开通/异常不挂
 * - await 前后双检查,避免与命令式/标签时序竞态
 */
export async function autoMountCustomerService(): Promise<void> {
  if (typeof document === 'undefined') return;
  const { customerService } = getConfig();
  if (!customerService) return;
  if (document.querySelector('hotel-customer-service')) return;
  if (currentHandle) return;

  const cs = await fetchCustomerServiceConfig();
  if (!cs) return; // 未开通/未配置/异常:不挂载
  // await 期间可能已命令式挂载或放了标签,再检查一次
  if (currentHandle) return;
  if (document.querySelector('hotel-customer-service')) return;

  mountCustomerService({
    tenantId: cs.tenantId,
    chatUrl: cs.chatUrl,
    style: DEFAULT_CS_STYLE,
  });
}
